import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Testspiel, TeamType } from '../../types'

interface TestspielStore {
  testspiele: Testspiel[]
  addTestspiel: (testspiel: Omit<Testspiel, 'id'>) => void
  updateTestspiel: (id: string, testspiel: Partial<Testspiel>) => void
  updateTestspielStatus: (id: string, status: 'pending' | 'confirmed' | 'cancelled', comment?: string) => void
  deleteTestspiel: (id: string) => void
  markAsSaved: (id: string) => void
  getTestspielById: (id: string) => Testspiel | undefined
  getTestspieleByTeam: (team: TeamType) => Testspiel[]
  isDateAvailableForTeam: (team: TeamType, date: string, excludeId?: string) => boolean
}

const useTestspielStore = create<TestspielStore>()(
  persist(
    (set, get) => ({
      testspiele: [
        {
          id: '1',
          team: '1. Mannschaft',
          opponent: 'FC Nachbarn',
          date: '2025-02-15',
          time: '14:00',
          location: 'Vereinsplatz FC Frick',
          trainerName: 'Hans Müller',
          notes: 'Wichtiges Testspiel vor der Saison',
          isSaved: false,
          status: 'pending',
        },
        {
          id: '2',
          team: 'FF-14',
          opponent: 'SV Waldorf',
          date: '2025-02-20',
          time: '16:00',
          location: 'Auswärtsspiel',
          trainerName: 'Julia Schmidt',
          notes: 'Erste Begegnung mit dem neuen Team',
          isSaved: true,
          status: 'confirmed',
        },
        {
          id: '3',
          team: '2. Mannschaft',
          opponent: 'FC Dorfverein',
          date: '2025-01-25',
          time: '10:00',
          location: 'Vereinsplatz FC Frick',
          trainerName: 'Peter Weber',
          notes: 'Vergangenes Testspiel - hat gut geklappt!',
          isSaved: true,
          status: 'confirmed',
        },
      ],

      addTestspiel: (testspiel) => {
        const newTestspiel: Testspiel = {
          ...testspiel,
          id: Date.now().toString(),
          isSaved: false,
          status: 'pending',
        }
        set((state) => ({
          testspiele: [...state.testspiele, newTestspiel]
        }))
      },

      updateTestspiel: (id, updatedTestspiel) => {
        set((state) => ({
          testspiele: state.testspiele.map((testspiel) =>
            testspiel.id === id ? { ...testspiel, ...updatedTestspiel } : testspiel
          )
        }))
      },

      updateTestspielStatus: (id, status, comment) => {
        set((state) => ({
          testspiele: state.testspiele.map((testspiel) =>
            testspiel.id === id ? { 
              ...testspiel, 
              status, 
              notes: comment ? `${testspiel.notes || ''}\n\nStatus-Update: ${comment}`.trim() : testspiel.notes 
            } : testspiel
          )
        }))
      },

      deleteTestspiel: (id) => {
        set((state) => ({
          testspiele: state.testspiele.filter((testspiel) => testspiel.id !== id)
        }))
      },

      markAsSaved: (id) => {
        set((state) => ({
          testspiele: state.testspiele.map((testspiel) =>
            testspiel.id === id ? { ...testspiel, isSaved: true } : testspiel
          )
        }))
      },

      getTestspielById: (id) => {
        return get().testspiele.find(testspiel => testspiel.id === id)
      },

      getTestspieleByTeam: (team) => {
        return get().testspiele.filter(testspiel => testspiel.team === team)
      },

      isDateAvailableForTeam: (team, date, excludeId) => {
        const { testspiele } = get()
        return !testspiele.some(testspiel => 
          testspiel.team === team && 
          testspiel.date === date && 
          (!excludeId || testspiel.id !== excludeId)
        )
      },
    }),
    {
      name: 'fc-frick-testspiele',
    }
  )
)

export { useTestspielStore }
